import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { VideoCamera, CalendarCheck, Crown } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDay = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

export default function BookingCalendar({ token }) {
  const [slots, setSlots] = useState(null);
  const [myBooking, setMyBooking] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const res = await fetch(`${API}/portal/${token}/slots`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSlots(data.slots);
      setMyBooking(data.my_booking);
    } catch {
      setError("Booking is unavailable right now — message us below instead.");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const days = useMemo(() => {
    if (!slots) return [];
    const map = new Map();
    slots.forEach((iso) => {
      const day = fmtDay(iso);
      if (!map.has(day)) map.set(day, []);
      map.get(day).push(iso);
    });
    return Array.from(map.entries());
  }, [slots]);

  const book = async () => {
    if (!selected) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/portal/${token}/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slot: selected }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Booking failed");
      setMyBooking(data);
      setSelected(null);
    } catch (e) {
      setError(typeof e.message === "string" ? e.message : "Booking failed — please try another slot.");
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-14" data-testid="booking-section">
      <div className="flex items-center gap-3">
        <Crown size={20} weight="duotone" className="text-accent" />
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-accent">Compass Members only</p>
      </div>
      <h2 className="font-serif text-3xl md:text-4xl text-primary tracking-tight mt-4" data-testid="booking-heading">
        The final step: a face-to-face call
      </h2>
      <p className="mt-3 text-sm text-foreground/60 max-w-xl leading-relaxed">
        When writing isn't enough, book a private video call with the team. Pick a time — your
        personal video link arrives by email before the call.
      </p>

      {myBooking ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 bg-primary text-primary-foreground rounded-sm p-8 flex items-start gap-5"
          data-testid="booking-success"
        >
          <CalendarCheck size={32} weight="duotone" className="text-accent shrink-0" />
          <div>
            <p className="font-serif text-2xl tracking-tight">You're booked</p>
            <p className="mt-2 text-sm text-primary-foreground/75" data-testid="booking-success-time">
              {fmtDay(myBooking.slot)} at {fmtTime(myBooking.slot)} (your local time)
            </p>
            <p className="mt-2 text-xs text-primary-foreground/50 leading-relaxed">
              Your video link will arrive by email before the call. To reschedule, just say so in your message thread above.
            </p>
          </div>
        </motion.div>
      ) : (
        <div className="mt-8 bg-card border border-border rounded-sm p-6 md:p-8" data-testid="booking-calendar">
          {error && <p className="mb-5 text-sm font-semibold text-destructive" data-testid="booking-error">{error}</p>}
          {!slots && !error && <div className="h-32 animate-pulse bg-muted rounded-sm" />}
          <div className="space-y-6 max-h-[380px] overflow-y-auto pr-2">
            {days.map(([day, daySlots]) => (
              <div key={day}>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{day}</p>
                <div className="mt-3 flex flex-wrap gap-2.5">
                  {daySlots.map((iso) => (
                    <button
                      key={iso}
                      data-testid={`booking-slot-${iso}`}
                      onClick={() => setSelected(iso)}
                      className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors duration-300 ${
                        selected === iso
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-foreground/70 hover:border-accent hover:text-primary"
                      }`}
                    >
                      {fmtTime(iso)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {slots && slots.length === 0 && (
              <p className="text-sm text-foreground/50" data-testid="booking-none">No open slots in the next two weeks — message us and we'll find you one.</p>
            )}
          </div>
          {selected && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6 flex flex-wrap items-center gap-4 border-t border-border pt-6">
              <p className="text-sm font-semibold text-foreground" data-testid="booking-selected">
                {fmtDay(selected)} at {fmtTime(selected)} (your local time)
              </p>
              <button
                data-testid="booking-confirm-button"
                onClick={book}
                disabled={busy}
                className="flex items-center gap-2 bg-accent text-accent-foreground px-7 py-3 rounded-full font-semibold text-sm hover:bg-primary hover:text-primary-foreground disabled:opacity-50 transition-colors duration-300"
              >
                <VideoCamera size={16} weight="bold" />
                {busy ? "Booking…" : "Confirm my call"}
              </button>
            </motion.div>
          )}
        </div>
      )}
    </section>
  );
}
